import { API_CONFIG } from '../config/env';

export interface BuyerLocation {
  payerCountryCode?: string | null;
  payerSubdivisionCode?: string | null;
}

interface CountryIsResponse {
  country?: string | null;
  subdivision?: string | null;
}

const normalizeCountryCode = (value?: string | null): string | null => {
  const normalized = value?.trim().toUpperCase() || '';
  return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
};

const normalizeSubdivisionCode = (value?: string | null): string | null => {
  const normalized = value?.trim().toUpperCase() || '';
  return /^[A-Z0-9-]{2,8}$/.test(normalized) ? normalized : null;
};

let cachedLocationPromise: Promise<BuyerLocation | null> | null = null;

const fetchBuyerLocation = async (): Promise<BuyerLocation | null> => {
  const geoUrl = API_CONFIG.BUYER_GEO_URL?.trim();
  if (!geoUrl) {
    return null;
  }

  const response = await fetch(geoUrl, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Buyer geo lookup failed with status ${response.status}.`);
  }

  const payload = await response.json() as CountryIsResponse;
  const payerCountryCode = normalizeCountryCode(payload.country);
  const payerSubdivisionCode = normalizeSubdivisionCode(payload.subdivision);

  if (!payerCountryCode && !payerSubdivisionCode) {
    return null;
  }

  return {
    payerCountryCode,
    payerSubdivisionCode,
  };
};

class BuyerLocationService {
  async resolve(): Promise<BuyerLocation | null> {
    if (!cachedLocationPromise) {
      cachedLocationPromise = fetchBuyerLocation().catch(() => null);
    }

    return cachedLocationPromise;
  }

  resetCache(): void {
    cachedLocationPromise = null;
  }
}

export const buyerLocationService = new BuyerLocationService();
